import { AxiosError } from 'axios';
import React, { useContext, useState } from 'react';

import AppContext from '@/contexts/appContext';
import PersonAvailabilityRepository from '@/repositories/PersonAvailabilityRepository';
import Button from '@/ui/components/Button';
import Modal from '@/ui/components/Modal';
import { WEEKDAY_NAMES, WEEKDAY_ORDER } from '@/utils/calendar';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import { convertTo24Hour, validateNoOverlappingIntervals } from '@/utils/timeFormat';

import type { Props } from './types';

const CopyTimesModal: React.FC<Props> = ({
  sourceWeekday,
  sourceIntervals,
  allRules,
  availabilityId,
  personId,
  timezone,
  onClose,
  onComplete
}) => {
  const { addMessage } = useContext(AppContext);
  const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set([sourceWeekday]));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const availabilityRepo = PersonAvailabilityRepository();

  const toggleDay = (weekday: string) => {
    const newSelected = new Set(selectedDays);
    if (newSelected.has(weekday)) {
      newSelected.delete(weekday);
    } else {
      newSelected.add(weekday);
    }
    setSelectedDays(newSelected);
  };

  const handleApply = async () => {
    setIsSubmitting(true);
    try {
      // Validate no overlapping intervals in source
      const overlapError = validateNoOverlappingIntervals(sourceIntervals);
      if (overlapError) {
        addMessage('error', overlapError);
        setIsSubmitting(false);
        return;
      }

      // Remove old rules for selected weekdays
      const otherRules = allRules.filter(
        (r) => r.type !== 'weekday' || !selectedDays.has(r.weekday!)
      );

      // Convert intervals to 24-hour format for API
      const formattedIntervals = sourceIntervals.map((interval) => ({
        from: convertTo24Hour(interval.from),
        to: convertTo24Hour(interval.to)
      }));

      // Add new rules for selected weekdays
      const newRules = Array.from(selectedDays).map((weekday) => ({
        type: 'weekday' as const,
        weekday: weekday,
        intervals: formattedIntervals
      }));

      const updatedRules = [...otherRules, ...newRules];

      if (availabilityId) {
        await availabilityRepo.update({
          id: availabilityId,
          data: {
            name: 'Member Availability',
            person_id: personId,
            timezone,
            rules: updatedRules
          }
        });
      } else {
        await availabilityRepo.create({
          data: {
            name: 'Member Availability',
            person_id: personId,
            timezone,
            rules: updatedRules
          }
        });
      }

      onComplete();
    } catch (error) {
      const messages = mapAxiosErrorMessages(error as AxiosError);
      messages.forEach((msg) => addMessage(msg.level, msg.text));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={true} onClose={onClose} title="Copy times to..." dataTestId="copy-times-modal">
      <div className="space-y-2">
        {WEEKDAY_ORDER.map((weekday) => (
          <label key={weekday} className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedDays.has(weekday)}
              onChange={() => toggleDay(weekday)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              aria-label={`Select ${WEEKDAY_NAMES[weekday]}`}
            />
            <span className="text-sm text-gray-900">{WEEKDAY_NAMES[weekday]}</span>
          </label>
        ))}
      </div>

      <div className="flex justify-end gap-3">
        <Button
          onClick={onClose}
          color="white"
          disabled={isSubmitting}
          ariaLabel="Cancel copying times">
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          color="blue"
          disabled={isSubmitting || selectedDays.size === 0}
          ariaLabel={`Apply times to ${selectedDays.size} selected day${selectedDays.size === 1 ? '' : 's'}`}>
          {isSubmitting ? 'Applying...' : 'Apply'}
        </Button>
      </div>
    </Modal>
  );
};

export default React.memo(CopyTimesModal);
